import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, getEventInfosByEventId, getTicketTypesByEventId, getTicketTypeInfosByTicketTypeId, getTicketTypeNumberOfAvailableTickets, getLocationInfos } from "ticketino-api-client";
import axios from "axios";

import Loading from "./components/Loading";

const Home = () => {
    const [ticketTypeIdRegister, setTicketTypeIdRegister] = useState(0);

    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState({});
    const [selectedEventId, setSelectedEventId] = useState(0);
    const [selectedTicketTypeId, setSelectedTicketTypeId] = useState(0);

    const [posId, setPosId] = useState(0);
    const [organizerId, setOrganizerId] = useState(0);

    const [tokenLoaded, setTokenLoaded] = useState(false);

    const [errors, setErrors] = useState("");

    const [loading, setLoading] = useState(false);

    // fetching resources
    const [resources, setResources] = useState({});

    // events ids for hardcoded data
    const event_november_2025_24 = 205146;
    const event_november_2025_25 = 192451;

    const [buyerInfo, setBuyerInfo] = useState({
        firstName: "",
        name: "",
        company: "",
        email: "",
        countryId: 176,
        mealPreference: "Mit Fleisch", // 'withMeat' | 'vegetarian' | 'other'
        mealOtherText: "",
        comment: "",
        membership: "",
        participation: "",
        workshops: [],
        arrival: "",
    });

    const [companionInfo, setCompanionInfo] = useState({
        salutationCompanion: "",
        titleCompanion: "",
        firstNameCompanion: "",
        nameCompanion: "",
        hasLunchCompanion: false,
        isVegetarianCompanion: false,
        isVeganCompanion: false,
    });

    // react hook for navigation
    let navigate = useNavigate();

    // base url
    let baseUrl = process.env.REACT_APP_BASEURL_API;

    // setting the base url of the npm package api calls
    api.defaults.baseURL = baseUrl;

    // fetching params
    const { language } = useParams();

    let languageId = 0;

    // changing languageId according to the url
    switch (language) {
        case ("de" || "DE"):
            languageId = 1;
            break;
        default:
            languageId = 0;
            language = "de";
            break;
    }

    useEffect(() => {
        loadToken();

        // clear the addedPromocodes in the session
        let array = [];
        sessionStorage.setItem("AddedPromotions", JSON.stringify(array));

    }, []); //only on first page load

    useEffect(() => {
        requestResources();
    }, [language]); //everytime language is changed

    useEffect(() => {
        if (!buyerInfo.hasLunch) {
            setBuyerInfo((prevBuyerInfo) => ({
                ...prevBuyerInfo,
                isVegetarian: false,
                isVegan: false
            }));

            setCompanionInfo((prevCompanionInfo) => ({
                ...prevCompanionInfo,
                hasLunchCompanion: false,
                isVegetarianCompanion: false,
                isVeganCompanion: false
            }));
        }
    }, [buyerInfo.hasLunch]);

    useEffect(() => {
        if (!companionInfo.hasLunchCompanion) {
            setCompanionInfo((prevCompanionInfo) => ({
                ...prevCompanionInfo,
                isVegetarianCompanion: false,
                isVeganCompanion: false
            }));
        }
    }, [companionInfo.hasLunchCompanion]);

    const loadToken = async () => {
        await axios.get("form/token").then((res) => {
            axios.defaults.headers.common["Authorization"] = "Bearer " + res.data;
            api.defaults.headers.common['Authorization'] = "Bearer " + res.data;

            sessionStorage.setItem("token", res.data);

            setTokenLoaded(true);
            requestFormSettings();
        });
    };

    const requestFormSettings = async () => {
        await axios.get(`form/formsettings`).then((res) => {
            // setting the posID and eventID etc
            setOrganizerId(res.data.organizerId);
            setPosId(res.data.posId);

            loadEvents(res.data.organizerId);
        });
    };

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.translation);
            })
            .catch((error) => console.error(error.response.data));
    };

    // fetching the events (to display it on the sidebar)
    const loadEvents = async (organizerId) => {
        setLoading(true);
        const date = new Date().toISOString().substring(0, 10);

        try {
            const eventsResponse = await axios.get(`${baseUrl}/Events?organizerId=${organizerId}&filterText=&fromDate=${date}`);
            let events = eventsResponse.data?.events || [];

            // Filter out inactive events
            events = events.filter(event => event.status !== 0);

            // Enrich each event with eventInfo, locationInfo, and ticketTypes with ticketTypeInfo
            const enrichedEvents = await Promise.all(events.map(async (event) => {
                const [eventInfo, locationInfos, ticketTypes] = await Promise.all([
                    requestEventInfo(event.id),
                    getLocationInfos(event.locationId),
                    getTicketTypesByEventId(event.id),
                ]);

                const locationInfo = locationInfos?.[0] || null;

                // Enrich ticket types with ticketTypeInfo
                const enrichedTicketTypes = await Promise.all(
                    (ticketTypes || []).map(async (tt) => {
                        const infos = await getTicketTypeInfosByTicketTypeId(tt.id);
                        return {
                            ...tt,
                            ticketTypeInfo: infos?.[0] || null,
                        };
                    })
                );

                return {
                    ...event,
                    eventInfo,
                    locationInfo,
                    ticketTypes: enrichedTicketTypes,
                };
            }));

            setEvents(enrichedEvents);

            // Auto-select first event if available
            if (enrichedEvents.length > 0) {
                const firstEvent = enrichedEvents[0];
                setSelectedEvent(enrichedEvents[0]);
                setSelectedEventId(enrichedEvents[0].id);

                const defaultTicketType = firstEvent.ticketTypes?.[0];
                if (defaultTicketType) {
                    setSelectedTicketTypeId(defaultTicketType.id);
                }
            }
        } catch (error) {
            console.error("Error fetching events:", error);
        } finally {
            setLoading(false);
        }
    };

    const requestEventInfo = async (id) => {
        try {
            const res = await axios.get(`${baseUrl}/Event/${id}/Infos`);
            return res.data.eventInfos?.[0] || null;
        } catch (error) {
            console.error(`Error fetching event info for event ${id}:`, error.response?.data || error.message);
            return null;
        }
    };

    const addTicketToBasket = async (ticketTypeId, orderId) => {
        const addTicketTypes = [{
            ticketTypeId,
            quantity: 1,
            firstname: buyerInfo.firstName,
            lastname: buyerInfo.name,
            company: buyerInfo.company,
            email: buyerInfo.email,
            option10: buyerInfo.mealPreference,
            option11: buyerInfo.mealOtherText,
            option12: buyerInfo.comment,
            option13: buyerInfo.participation,
            option14: buyerInfo.workshops.join("; "),
            option15: buyerInfo.arrival,
            option16: buyerInfo.hotel,
        }];

        try {
            await axios.post(`${baseUrl}/ShopBasket/Order/${orderId}/Tickets`, {
                ticketsToAdd: addTicketTypes,
            });

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: 'addToCart' });

            await addAddressToBasket(orderId);
        } catch (error) {
            console.error("Error adding tickets:", error.response?.data || error.message);
        }
    };

    const addAddressToBasket = async (orderId) => {
        const addressBody = { ...buyerInfo };

        try {
            await axios.put(`${baseUrl}/Order/${orderId}/Address`, addressBody);

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: 'addAddress' });



            const updatedOrder = await getOrderByOrderId(orderId);

            if (updatedOrder?.totalPrice === 0) {
                await finishFreeOrder(orderId);
            } else {
                navigate(`/${language}/checkout`);
            }
        } catch (error) {
            console.error("Error updating address or fetching order:", error.response?.data || error.message);
        }
    };

    const getOrderByOrderId = async (orderId) => {
        try {
            const res = await axios.get(`${baseUrl}/ShopBasket/Order/${orderId}`);
            return res.data;
        } catch (error) {
            console.error("Failed to fetch order:", error.response?.data || error.message);
            return null;
        }
    };

    const finishFreeOrder = async (orderId) => {
        try {
            await axios.put(`${baseUrl}/ShopBasket/Order/${orderId}/PaymentType/5`);
            await confirmShopbasketByOrderId(orderId);
            navigate(`/${language}/confirmation/${orderId}`);
        } catch (error) {
            console.error("Error finishing free order:", error.response?.data || error.message);
        }
    };

    const confirmShopbasketByOrderId = async (orderId) => {
        try {
            const res = await axios.put(`${baseUrl}/ShopBasket/Order/${orderId}/Confirm`);
        } catch (error) {
            console.error(error)
        }
    };

    const onSubmit = async () => {
        if (validateEntries()) {
            try {
                // Only start the order now
                let order = {
                    affiliateShopId: null,
                    currency: "CHF",
                    tenantId: 1,
                    languageId: languageId,
                    pointOfSaleId: posId,
                    abbreviation: "",
                    paymentType: 1,
                };

                const res = await axios.post(`${baseUrl}/ShopBasket/Order`, order);
                const orderId = res.data.id;
                sessionStorage.setItem("orderId", orderId);

                await addTicketToBasket(selectedTicketTypeId, orderId);
            } catch (error) {
                console.error("Order start failed:", error);
            }
        }
    };

    const handleEventChange = (e) => {
        const eventId = Number(e.target.value);
        setSelectedEventId(eventId);
        const selectedEvent = events.find(ev => ev.id === eventId);
        setSelectedEvent(selectedEvent);

        const defaultTicketType = selectedEvent?.ticketTypes?.[0];
        if (defaultTicketType) {
            setSelectedTicketTypeId(defaultTicketType.id);
        }

        // Reset buyerInfo when event changes
        setBuyerInfo({
            firstName: "",
            name: "",
            company: "",
            email: "",
            countryId: 176,
            mealPreference: "Mit Fleisch",
            mealOtherText: "",
            comment: "",
            membership: "",
            participation: "",
            workshops: [],
            arrival: "",
        });
    };

    const handleMembershipChange = (e) => {
        const ticketTypeId = Number(e.target.value);
        setSelectedTicketTypeId(ticketTypeId);
    };

    const handleMealPreferenceChange = (e) => {
        const value = e.target.value;
        setBuyerInfo(prev => ({
            ...prev,
            mealPreference: value,
            mealOtherText: value === resources?.BuyerMealOther ? prev.mealOtherText : "", // reset otherText unless "other"
        }));
    };

    const onInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setBuyerInfo((prevBuyerInfo) => {
            return { ...prevBuyerInfo, [name]: type === 'checkbox' ? checked : value };
        });
    };

    const validateEntries = () => {

        if (!(buyerInfo.firstName && /^.{1,70}$/.test(buyerInfo.firstName)) ||
            !(buyerInfo.name && /^.{1,70}$/.test(buyerInfo.name)) ||
            !(buyerInfo.company && /^.{1,70}$/.test(buyerInfo.company))) {
            setErrors(resources?.FillMandatoryFields);
            return false;
        }
        else if (!(buyerInfo.email && /^\S+@\S+\.\S+$/.test(buyerInfo.email))) {
            setErrors(resources?.EmailNotCorrect);
            return false;
        }

        // Extra validations only for event_november_2025_24
        if (selectedEventId === event_november_2025_24) {
            //if (!buyerInfo.participation) {
            //    setErrors("Bitte gib an, ob du an der Jubiläumsparty teilnimmst.");
            //    return false;
            //}

            //if (!buyerInfo.hotel) {
            //    setErrors("Bitte wähle eine Option für die Hotelübernachtung.");
            //    return false;
            //}

            if (!buyerInfo.arrival) {
                setErrors("Bitte gib an, ob du den Transfer in Anspruch nimmst.");
                return false;
            }
        }

        // Extra validations only for event_november_2025_25
        if (selectedEventId === event_november_2025_25) {
            if (buyerInfo.workshops.length !== 2) {
                setErrors("Bitte wähle genau zwei Workshops aus.");
                return false;
            }

            //if (!buyerInfo.participation) {
            //    setErrors("Bitte gib an, ob du am Circle oder nur an der Party teilnimmst.");
            //    return false;
            //}

            if (!buyerInfo.arrival) {
                setErrors("Bitte gib an, wie du anreist.");
                return false;
            }
        }

        setErrors("");
        return true;
    }

    const EventInfoBox = ({ event }) => {
        if (!event) return null;

        return (
            <div id="event-info">
                <span>{event?.eventInfo?.name}</span>
                <hr />
                <small>
                    {event?.openDoor &&
                        new Date(event.openDoor).toLocaleDateString("de-DE", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                        })}
                </small>,{" "}
                <small>
                    {event?.openDoor &&
                        new Date(event.openDoor).toLocaleTimeString("de-DE", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                        })}
                </small>
                {" – "}
                <small>
                    {event?.end &&
                        new Date(event.end).toLocaleTimeString("de-DE", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                        })}
                </small>
                <br />
                <small>
                    {event?.locationInfo?.name},{" "}
                    {event?.locationInfo?.street}{" "}
                    {event?.locationInfo?.streetNumber},{" "}
                    {event?.locationInfo?.postalCode}{" "}
                    {event?.locationInfo?.city}
                </small>
            </div>
        );
    };


    /*customized methods for hardcoded data because of lack of time*/

    const handleRadioChange_HardcodedData = (e) => {
        const { name, value } = e.target;
        setBuyerInfo(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleWorkshopChange = (e) => {
        const { value, checked } = e.target;

        setBuyerInfo((prev) => {
            const current = prev.workshops;

            // Already selected
            const isSelected = current.includes(value);

            if (checked) {
                // Allow only if fewer than 2 selected
                if (current.length < 2) {
                    return { ...prev, workshops: [...current, value] };
                }
            } else {
                // Allow unchecking only if more than 2 are selected
                if (current.length > 2 || isSelected) {
                    return { ...prev, workshops: current.filter((v) => v !== value) };
                }
            }

            return prev; // No change
        });
    };

    return (
        <div>
            {loading ? (
                <Loading
                    alignment="center"
                    color="#d3d3d3"
                    bgColor="#fff"
                    top="50%"
                />
            ) : (
                <>
                    <div className="container wrapper mb-2">
                        <div className="row">
                            <div className="col-md-8">
                                <h2 className="fw-bold">{resources?.HomeTitle}</h2>

                                {selectedEventId === event_november_2025_24 &&
                                    <div className="mt-4">
                                        <h2>Jubiläumsparty - 24. November 2025</h2>
                                        <span>VersuchsStollen Hagerbach, Flums (VSH)</span>
                                    </div>
                                }

                                {selectedEventId === event_november_2025_25 &&
                                    <div className="mt-4">
                                        <h2>Circle 25. November 2025</h2>
                                        <span>VersuchsStollen Hagerbach, Flums (VSH)</span>
                                    </div>
                                }

                                {/* event info box mobile */}
                                <div className="event-info-mobile mt-3">
                                    <EventInfoBox event={selectedEvent} />
                                </div>

                                <div className="form-group mt-3">
                                    <label>{resources?.HomeDates}</label>
                                    <select id="date" className="form-select" value={selectedEventId} onChange={handleEventChange} required>
                                        {events
                                            .filter(event => event.eventInfo?.name)
                                            .map(event => (
                                                <option
                                                    key={event.id}
                                                    value={event.id}
                                                >
                                                    {event.eventInfo.name}
                                                </option>
                                            ))}
                                    </select>
                                </div>

                                <div className="row">
                                    <div className="form-group mt-3 col-md-6">
                                        <label>*{resources?.BuyerFirstname}</label>
                                        <input
                                            type="text"
                                            name="firstName"
                                            className="form-control"
                                            value={buyerInfo.firstName}
                                            id="firstName"
                                            onChange={onInputChange}
                                        />
                                    </div>

                                    <div className="form-group mt-3 col-md-6">
                                        <label>*{resources?.BuyerLastname}</label>
                                        <input
                                            type="text"
                                            name="name"
                                            className="form-control"
                                            value={buyerInfo.name}
                                            id="name"
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="form-group mt-3 col-md-12">
                                        <label>*{resources?.BuyerEmail}</label>
                                        <input
                                            type="text"
                                            name="email"
                                            className="form-control"
                                            value={buyerInfo.email}
                                            id="email"
                                            disabled={false}
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="form-group mt-3 col-md-12">
                                        <label>*{resources?.BuyerCompany}</label>
                                        <input
                                            type="text"
                                            name="company"
                                            className="form-control"
                                            value={buyerInfo.company}
                                            id="company"
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="form-group mt-3">
                                    <label>{resources?.HomeMembership}</label>
                                    <select id="dllMembership"
                                        className="form-select"
                                        value={selectedTicketTypeId}
                                        onChange={handleMembershipChange}
                                        required
                                    >
                                        {selectedEvent?.ticketTypes?.map((ticketType) => (
                                            <option key={ticketType.id} value={ticketType.id}>
                                                {ticketType.ticketTypeInfo?.name || `Ticket ${ticketType.id}`}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group mt-3">
                                    <label className="mb-2 d-block">{resources?.BuyerMeal}</label>

                                    <div className="form-check mb-1">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="mealPreference"
                                            id="mealWithMeat"
                                            value={resources?.BuyerMealWithMeat}
                                            checked={buyerInfo.mealPreference === resources?.BuyerMealWithMeat}
                                            onChange={handleMealPreferenceChange}
                                        />
                                        <label className="form-check-label" htmlFor="mealWithMeat">
                                            {resources?.BuyerMealWithMeat}
                                        </label>
                                    </div>

                                    <div className="form-check mb-1">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="mealPreference"
                                            id="mealVegetarian"
                                            value={resources?.BuyerMealVegeterian}
                                            checked={buyerInfo.mealPreference === resources?.BuyerMealVegeterian}
                                            onChange={handleMealPreferenceChange}
                                        />
                                        <label className="form-check-label" htmlFor="mealVegetarian">
                                            {resources?.BuyerMealVegeterian}
                                        </label>
                                    </div>

                                    <div className="form-check mb-2">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="mealPreference"
                                            id="mealOther"
                                            value={resources?.BuyerMealOther}
                                            checked={buyerInfo.mealPreference === resources?.BuyerMealOther}
                                            onChange={handleMealPreferenceChange}
                                        />
                                        <label className="form-check-label" htmlFor="mealOther">
                                            {resources?.BuyerMealOther}
                                        </label>
                                    </div>

                                    <div className="mt-3">
                                        <input
                                            type="text"
                                            className="form-control txt-meal-other"
                                            value={buyerInfo.mealOtherText}
                                            name="mealOtherText"
                                            id="mealOtherText"
                                            onChange={onInputChange}
                                            disabled={buyerInfo.mealPreference !== resources?.BuyerMealOther}
                                        />
                                    </div>
                                </div>

                                <div className="row mt-3">
                                    <div className="form-group mt-3 col-md-12">
                                        <label>{resources?.HomeComment}</label>
                                        <textarea
                                            rows={3}
                                            type="textfield"
                                            name="comment"
                                            className="form-control"
                                            value={buyerInfo.comment}
                                            id="comment"
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>
                                {/* end main participant */}

                                {/* start hardcoded customized part that is displayed depending on eventId because of lack of time */}
                                {selectedEventId === event_november_2025_24 &&
                                    <div className="mt-4">

                                        {/* participation */}
                                        {/*<h2 className="mt-4">Teilnahme:</h2>*/}
                                        {/*<div className="form-check mb-1">*/}
                                        {/*    <input*/}
                                        {/*        className="form-check-input"*/}
                                        {/*        type="radio"*/}
                                        {/*        name="participation"*/}
                                        {/*        id="participation1_Yes"*/}
                                        {/*        value="Ich nehme an der Jubiläumsparty teil"*/}
                                        {/*        checked={buyerInfo.participation === "Ich nehme an der Jubiläumsparty teil"}*/}
                                        {/*        onChange={handleRadioChange_HardcodedData}*/}
                                        {/*    />*/}
                                        {/*    <label className="form-check-label" htmlFor="participation1_Yes">*/}
                                        {/*        Ich nehme an der Jubiläumsparty teil*/}
                                        {/*    </label>*/}
                                        {/*</div>*/}
                                        {/*<div className="form-check mb-1">*/}
                                        {/*    <input*/}
                                        {/*        className="form-check-input"*/}
                                        {/*        type="radio"*/}
                                        {/*        name="participation"*/}
                                        {/*        id="participation1_No"*/}
                                        {/*        value="Ich kann leider nicht teilnehmen"*/}
                                        {/*        checked={buyerInfo.participation === "Ich kann leider nicht teilnehmen"}*/}
                                        {/*        onChange={handleRadioChange_HardcodedData}*/}
                                        {/*    />*/}
                                        {/*    <label className="form-check-label" htmlFor="participation1_No">*/}
                                        {/*        Ich kann leider nicht teilnehmen*/}
                                        {/*    </label>*/}
                                        {/*</div>*/}

                                        {/* hotel booking */}
                                        <h2 className="mt-4">Hotelübernachtung:</h2>
                                        <span>Die Frist für die Hotelbuchung über uns, mit Spezialpreis ist leider abgelaufen.
                                            Wir empfehlen die folgenden 3 Hotels:</span><br /><br />
                                        <span>Hotel Franz Anton, Sargans</span><br />
                                        <span>Hotel Post, Sargans</span><br />
                                        <span>Hotel Schweizerhof, Mels</span><br /><br />
                                        <span>Der von uns organsierte Transfer, fährt alle 3 Hotels an.</span><br />

                                        {/*<span>(Die Hotelbuchung erfolgt durch den ECC. Die Kosten sind von den Teilnehmenden vor Ort direkt im Hotel zu begleichen. Frühstück ist inbegriffen. Preise gelten für Einzelzimmer).</span>*/}
                                        {/*<div className="form-check mt-3 mb-1">*/}
                                        {/*    <input*/}
                                        {/*        className="form-check-input"*/}
                                        {/*        type="radio"*/}
                                        {/*        name="hotel"*/}
                                        {/*        id="hotel1"*/}
                                        {/*        value="Hotel Franz Anton, Sargans – CHF 120.00"*/}
                                        {/*        checked={buyerInfo.hotel === "Hotel Franz Anton, Sargans – CHF 120.00"}*/}
                                        {/*        onChange={handleRadioChange_HardcodedData}*/}
                                        {/*    />*/}
                                        {/*    <label className="form-check-label" htmlFor="hotel1">*/}
                                        {/*        Hotel Franz Anton, Sargans – CHF 120.00*/}
                                        {/*    </label>*/}
                                        {/*</div>*/}
                                        {/*<div className="form-check mb-1">*/}
                                        {/*    <input*/}
                                        {/*        className="form-check-input"*/}
                                        {/*        type="radio"*/}
                                        {/*        name="hotel"*/}
                                        {/*        id="hotel2"*/}
                                        {/*        value="Hotel Post, Sargans – CHF 80.00"*/}
                                        {/*        checked={buyerInfo.hotel === "Hotel Post, Sargans – CHF 80.00"}*/}
                                        {/*        onChange={handleRadioChange_HardcodedData}*/}
                                        {/*    />*/}
                                        {/*    <label className="form-check-label" htmlFor="hotel2">*/}
                                        {/*        Hotel Post, Sargans – CHF 80.00*/}
                                        {/*    </label>*/}
                                        {/*</div>*/}
                                        {/*<div className="form-check mb-1">*/}
                                        {/*    <input*/}
                                        {/*        className="form-check-input"*/}
                                        {/*        type="radio"*/}
                                        {/*        name="hotel"*/}
                                        {/*        id="hotel3"*/}
                                        {/*        value="Hotel Schweizerhof, Mels – CHF 110.00"*/}
                                        {/*        checked={buyerInfo.hotel === "Hotel Schweizerhof, Mels – CHF 110.00"}*/}
                                        {/*        onChange={handleRadioChange_HardcodedData}*/}
                                        {/*    />*/}
                                        {/*    <label className="form-check-label" htmlFor="hotel3">*/}
                                        {/*        Hotel Schweizerhof, Mels – CHF 110.00*/}
                                        {/*    </label>*/}
                                        {/*</div>*/}
                                        {/*<div className="form-check mb-1">*/}
                                        {/*    <input*/}
                                        {/*        className="form-check-input"*/}
                                        {/*        type="radio"*/}
                                        {/*        name="hotel"*/}
                                        {/*        id="hotel4"*/}
                                        {/*        value="Ich benötige kein Hotel"*/}
                                        {/*        checked={buyerInfo.hotel === "Ich benötige kein Hotel"}*/}
                                        {/*        onChange={handleRadioChange_HardcodedData}*/}
                                        {/*    />*/}
                                        {/*    <label className="form-check-label" htmlFor="hotel4">*/}
                                        {/*        Ich benötige kein Hotel*/}
                                        {/*    </label>*/}
                                        {/*</div>*/}

                                        {/* Transfer zur Party */}
                                        <h2 className="mt-4">Transfer zur Party:</h2>
                                        <span>Hin: Abfahrt bei den Hotels um 18:15 Uhr</span><br />
                                        <span>Rückfahrt: VSH ab 22:00 Uhr im 30-Minuten-Takt (letzte Fahrt 23:30 Uhr)</span>

                                        <div className="form-check mt-3 mb-1">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="arrival"
                                                id="arrival_transfer1"
                                                value="Ich nehme den Transfer in Anspruch"
                                                checked={buyerInfo.arrival === "Ich nehme den Transfer in Anspruch"}
                                                onChange={handleRadioChange_HardcodedData}
                                            />
                                            <label className="form-check-label" htmlFor="arrival_transfer1">
                                                Ich nehme den Transfer in Anspruch
                                            </label>
                                        </div>
                                        <div className="form-check mb-1">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="arrival"
                                                id="arrival_transfer2"
                                                value="Ich benötige keinen Transfer"
                                                checked={buyerInfo.arrival === "Ich benötige keinen Transfer"}
                                                onChange={handleRadioChange_HardcodedData}
                                            />
                                            <label className="form-check-label" htmlFor="arrival_transfer2">
                                                Ich benötige keinen Transfer
                                            </label>
                                        </div>
                                        <label className="form-check-label mt-2" htmlFor="arrival_transfer2">
                                            <div>
                                                <strong>Hinweis:</strong> Die Anmeldung ist verbindlich. Eine kostenlose Abmeldung ist bis spätestens am 17. November 2025 möglich. Danach werden CHF 80.00 sowie ggf. Hotelkosten verrechnet.
                                            </div>
                                        </label>
                                    </div>
                                }

                                {selectedEventId === event_november_2025_25 &&
                                    <div className="mt-4">

                                        {/* participation */}
                                        {/*<h2 className="mt-4">Teilnahme:</h2>*/}
                                        {/*<div className="form-check mb-1">*/}
                                        {/*    <input*/}
                                        {/*        className="form-check-input"*/}
                                        {/*        type="radio"*/}
                                        {/*        name="participation"*/}
                                        {/*        id="participation_Yes"*/}
                                        {/*        value="Ich nehme am Circle teil"*/}
                                        {/*        checked={buyerInfo.participation === "Ich nehme am Circle teil"}*/}
                                        {/*        onChange={handleRadioChange_HardcodedData}*/}
                                        {/*    />*/}
                                        {/*    <label className="form-check-label" htmlFor="participation_Yes">*/}
                                        {/*        Ich nehme am Circle teil*/}
                                        {/*    </label>*/}
                                        {/*</div>*/}
                                        {/*<div className="form-check mb-1">*/}
                                        {/*    <input*/}
                                        {/*        className="form-check-input"*/}
                                        {/*        type="radio"*/}
                                        {/*        name="participation"*/}
                                        {/*        id="participation_No"*/}
                                        {/*        value="Ich nehme nur an der Party teil"*/}
                                        {/*        checked={buyerInfo.participation === "Ich nehme nur an der Party teil"}*/}
                                        {/*        onChange={handleRadioChange_HardcodedData}*/}
                                        {/*    />*/}
                                        {/*    <label className="form-check-label" htmlFor="participation_No">*/}
                                        {/*        Ich nehme nur an der Party teil*/}
                                        {/*    </label>*/}
                                        {/*</div>*/}

                                        {/* workshops */}
                                        <h2 className="mt-4">Workshops:</h2>
                                        <div className="form-check mb-1">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="workshop1"
                                                value="Workshop 1: Wie schaffen Menschen wirklich Neues?"
                                                checked={buyerInfo.workshops.includes("Workshop 1: Wie schaffen Menschen wirklich Neues?")}
                                                onChange={handleWorkshopChange}
                                                disabled={
                                                    !buyerInfo.workshops.includes("Workshop 1: Wie schaffen Menschen wirklich Neues?") &&
                                                    buyerInfo.workshops.length >= 2
                                                }
                                            />
                                            <label className="form-check-label" htmlFor="workshop1">
                                                <div>
                                                    <strong>Workshop 1: Wie schaffen Menschen wirklich Neues?</strong><br />
                                                    <span>Tobias Eismann, Siemens AG</span><br />
                                                    <span>siemens.com</span>
                                                </div>
                                            </label>
                                        </div>
                                        <div className="form-check mb-1">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="workshop2"
                                                value="Workshop 2: www.wissen-wie-wirken"
                                                checked={buyerInfo.workshops.includes("Workshop 2: www.wissen-wie-wirken")}
                                                onChange={handleWorkshopChange}
                                                disabled={
                                                    !buyerInfo.workshops.includes("Workshop 2: www.wissen-wie-wirken") &&
                                                    buyerInfo.workshops.length >= 2
                                                }
                                            />
                                            <label className="form-check-label" htmlFor="workshop2">
                                                <div>
                                                    <strong>Workshop 2:  www – wissen · wie · wirken</strong><br />
                                                    <span>Simone C. Styger, The Style Connection</span><br />
                                                    <span>styleconnection.ch, stilexpertin.ch</span>
                                                </div>
                                            </label>
                                        </div>
                                        <div className="form-check mb-1">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="workshop3"
                                                value="Workshop 3: Chat GPT Masterclass"
                                                checked={buyerInfo.workshops.includes("Workshop 3: Chat GPT Masterclass")}
                                                onChange={handleWorkshopChange}
                                                disabled={
                                                    !buyerInfo.workshops.includes("Workshop 3: Chat GPT Masterclass") &&
                                                    buyerInfo.workshops.length >= 2
                                                }
                                            />
                                            <label className="form-check-label" htmlFor="workshop3">
                                                <div>
                                                    <strong>Workshop 3: Chat GPT Masterclass</strong><br />
                                                    <span>Maks Giordano, Juuman’Okudo GmbH</span><br />
                                                    <span>juumanokudo.com</span>
                                                </div>
                                            </label>
                                        </div>
                                        <div className="form-check mb-1">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="workshop4"
                                                value="Workshop 4: Sicher auftreten – Souverän wirken"
                                                checked={buyerInfo.workshops.includes("Workshop 4: Sicher auftreten – Souverän wirken")}
                                                onChange={handleWorkshopChange}
                                                disabled={
                                                    !buyerInfo.workshops.includes("Workshop 4: Sicher auftreten – Souverän wirken") &&
                                                    buyerInfo.workshops.length >= 2
                                                }
                                            />
                                            <label className="form-check-label" htmlFor="workshop4">
                                                <div>
                                                    <strong>Workshop 4: Sicher auftreten – Souverän wirken</strong><br />
                                                    <span>Sonja Randjelovic, Zürichsee Schifffahrtsgesellschaft</span><br />
                                                    <span>zsg.ch</span>
                                                </div>
                                            </label>
                                        </div>

                                        {/* Anreise zum Circle */}
                                        <h2 className="mt-4">Anreise zum Circle:</h2>
                                        <div className="form-check mb-1">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="arrival"
                                                id="arrival_self"
                                                value="Ich reise selber an"
                                                checked={buyerInfo.arrival === "Ich reise selber an"}
                                                onChange={handleRadioChange_HardcodedData}
                                            />
                                            <label className="form-check-label" htmlFor="arrival_self">
                                                Ich reise selber an
                                            </label>
                                        </div>
                                        <div className="form-check mb-1">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="arrival"
                                                id="arrival_transfer"
                                                value="Ich nutze den Transfer vom/zum Bahnhof Sargans"
                                                checked={buyerInfo.arrival === "Ich nutze den Transfer vom/zum Bahnhof Sargans"}
                                                onChange={handleRadioChange_HardcodedData}
                                            />
                                            <label className="form-check-label" htmlFor="arrival_transfer">
                                                <div>
                                                    Ich nutze den Transfer vom/zum Bahnhof Sargans<br />
                                                    <strong>•</strong> Hinfahrt ab Bahnhof Sargans: <strong>08:40 Uhr</strong><br />
                                                    <strong>•</strong> Rückfahrt ab VSH: <strong>16:20 Uhr</strong><br /><br />
                                                </div>
                                            </label>
                                        </div>
                                    </div>
                                }
                                {/* end hardcoded customized part because of lack of time */}

                                <div className="form-group">
                                    <button type="submit" className="btn btn-emc form-control mt-3" onClick={() => onSubmit()}>{resources?.HomeRegister}</button>
                                </div>
                            </div>

                            {/* event info box desktop */}
                            <div className="col-md-4 event-info-desktop">
                                <EventInfoBox event={selectedEvent} />
                            </div>
                        </div>

                        {errors !== "" && (
                            <div className="row text-start mt-4">
                                <div className="offset-md-6 col-md-6"></div>
                                <p className="text-danger ps-2">*{errors}</p>
                            </div>
                        )}

                        <p className="mt-4">
                            <small style={{ color: 'grey', fontSize: '10px' }}>powered by <a style={{ color: 'grey', textDecoration: 'none', fontSize: '13px' }} href="https://organizer.ticketino.com/de" target="_blank">TICKETINO</a></small>
                        </p>
                    </div>
                </>
            )}
        </div>
    )
}

export default Home